import { Controller } from '@hotwired/stimulus';
import { trans } from '../../../../../assets/translator.js';

import {
    run as cc_run,
    reset as cc_reset,
    getCookie as cc_getCookie,
} from 'vanilla-cookieconsent';

import '@fify/websites-bundle/styles/cookieconsent.css';


const CAT_NECESSARY = 'necessary';
const CAT_ANALYTICS = 'analytics';
const CAT_ADS       = 'ads';


export default class CookieConsentController extends Controller {
    static values = {
        tosLink:     String,
        privacyLink: String,

        reCaptcha: { type: Boolean, default: false },
        analytics: { type: Array, default: [] },
        ads:       { type: Array, default: [] },
        revision:  { type: Number, default: 2 },

        necessaryCookies: { type: Array, default: [] },
        analyticsCookies: { type: Array, default: [] },
        adsCookies:       { type: Array, default: [] },
    };

    /** @type {CookieConsentConfig} */
    static _config;

    /**
     * @returns {String}
     */
    get privacyLink() {
        return this.privacyLinkValue;
    }

    /**
     * @returns {String}
     */
    get tosLink() {
        return this.tosLinkValue;
    }

    /**
     * @returns {Boolean}
     */
    get reCaptcha() {
        return this.reCaptchaValue;
    }

    /**
     * @returns {{[key: string]: import('vanilla-cookieconsent').Service}}
     */
    get necessaryCookies() {
        return this._buildServices(this.necessaryCookiesValue);
    }

    /**
     * @returns {{[key: string]: import('vanilla-cookieconsent').Service}}
     */
    get analyticsCookies() {
        return this._buildServices(this.analyticsCookiesValue);
    }

    /**
     * @returns {{[key: string]: import('vanilla-cookieconsent').Service}}
     */
    get adsCookies() {
        return this._buildServices(this.adsCookiesValue);
    }

    /**
     * @returns {{[key: string]: import('vanilla-cookieconsent').Service}}
     */
    _buildServices(source) {
        const services = {};

        for (const cookie of source) {
            services[cookie.name] = {
                label:   cookie.label,
                cookies: cookie.cookies.map(regexp => ({ name: new RegExp(regexp) })),
            };
        }

        return services;
    }

    /**
     * @returns {Array}
     */
    get analytics() {
        return this.analyticsValue;
    }

    /**
     * @returns {Array}
     */
    get ads() {
        return this.adsValue;
    }

    /**
     * @returns {Number}
     */
    get revision() {
        return this.revisionValue;
    }


    initialize() {
        window.dispatchEvent(new CustomEvent('cc:onInitialize', {
            detail: {
                cookie: cc_getCookie(),
            },
        }));
    }

    async connect() {
        window.addEventListener('website:theme:change', this._themeChanged);

        if ((typeof localStorage === 'object' && localStorage.theme === 'dark')
            || document.documentElement.classList.contains('dark')
        ) {
            this.element.classList.add('cc--darkmode');
        }

        const config    = this._buildConfig();
        config.root     = this.element;
        config.autoShow = this._shouldShow();

        await cc_run(config);
        window.cookie_consent_initial_state = cc_getCookie();
    }

    disconnect() {
        cc_reset();
        window.removeEventListener('website:theme:change', this._themeChanged);
    }

    _themeChanged = (event) => {
        this._setDarkMode(event.detail?.theme === 'dark');
    };

    /**
     * @param {boolean} set
     * @private
     */
    _setDarkMode(set) {
        if (set === true) {
            this.element.classList.add('cc--darkmode');
        } else {
            this.element.classList.remove('cc--darkmode');
        }
    }

    /**
     * @returns {Boolean}
     * @private
     */
    _shouldShow() {
        return window.location.href !== (new URL(this.tosLink, window.location.href)).toString()
            && window.location.href !== (new URL(this.privacyLink, window.location.href)).toString();
    }

    /**
     * @returns {CookieConsentConfig}
     * @private
     */
    _buildConfig() {
        if (CookieConsentController._config) {
            return CookieConsentController._config;
        }

        return CookieConsentController._config = {
            revision: this.revision,

            disablePageInteraction: true,

            guiOptions: {
                consentModal: {
                    layout:             'box',
                    position:           'middle center',
                    equalWeightButtons: true,
                    flipButtons:        false,
                },

                preferencesModal: {
                    layout:             'bar wide',
                    position:           'left',
                    equalWeightButtons: true,
                    flipButtons:        false,
                },
            },

            categories: this._buildCategories(),

            language: {
                default:      'symfony',
                translations: {
                    symfony: this._buildTranslations(),
                },
            },
        };
    }

    /**
     * @return {Object<Category>}
     * @private
     */
    _buildCategories() {
        const categories = {
            [CAT_NECESSARY]: {
                readOnly: true,
                services: {
                    ...this.necessaryCookies,

                    csrf_protection: {
                        label:   trans('cookie_consent.service.csrf_protection'),
                        cookies: [
                            { name: /^__Host-crsf-token_/ },
                        ],
                    },
                },
            },
        };

        if (this.reCaptcha) {
            categories[CAT_NECESSARY].services.google_recaptcha = {
                label:   'Google reCAPTCHA',
                cookies: [
                    { name: '_GRECAPTCHA' },
                ],
            };
        }


        const analyticsCookies = this.analyticsCookies;

        if (analyticsCookies.length || this.analytics.length) {
            categories[CAT_ANALYTICS] = {
                enabled:  false,
                services: analyticsCookies,
            };

            if (this._hasGoogleAnalytics()) {
                categories[CAT_ANALYTICS].services.google_analytics = {
                    label:   'Google Analytics',
                    cookies: [
                        { name: /^(_gad|_gid|_gcl_)/ },
                    ],
                }
            }

            if (this._hasMatomo()) {
                categories[CAT_ANALYTICS].services.matomo = {
                    label:   'Matomo',
                    cookies: [
                        { name: /^(_pk_id|_pk_ses)/ },
                    ],
                }
            }
        }


        const adsCookies = this.adsCookies;

        if (adsCookies.length || this._hasGoogleAds()) {
            categories[CAT_ADS] = {
                enabled:  false,
                services: {
                    ...adsCookies,

                    ad_storage:         {
                        label:   trans('cookie_consent.service.ad_storage'),
                        cookies: [
                            { name: /^(_gads|ANID)/ },
                        ],
                    },
                    ad_personalization: {
                        label:   trans('cookie_consent.service.ad_personalization'),
                        cookies: [
                            { name: /^(IDE|FPGCLAW|FPGCLGB|FPGSID)/ },
                        ],
                    },
                    ad_user_data:       {
                        label:   trans('cookie_consent.service.ad_user_data'),
                        cookies: [
                            { name: /^(AID)/ },
                        ],
                    },
                },
            };
        }


        return categories;
    }

    /**
     * @returns {boolean}
     * @private
     */
    _hasGoogleAnalytics() {
        return this.analytics.indexOf('google') > -1;
    }

    /**
     * @returns {boolean}
     * @private
     */
    _hasGoogleAds() {
        return this.ads.indexOf('google') > -1;
    }

    /**
     * @returns {boolean}
     * @private
     */
    _hasMatomo() {
        return this.analytics.indexOf('matomo') > -1;
    }

    /**
     * @return {Translation}
     * @private
     */
    _buildTranslations() {
        const sections = [
            {
                title:       trans('cookie_consent.section.start.title'),
                description: trans('cookie_consent.section.start.description'),
            },
            {
                title:          trans('cookie_consent.section.necessary.title') + ` <span class="pm__badge">${trans('cookie_consent.always_enabled')}</span>`,
                description:    trans('cookie_consent.section.necessary.description'),
                linkedCategory: CAT_NECESSARY,

                cookieTable: {
                    headers: {
                        name:        'Cookie',
                        description: 'Description',
                    },
                    body: [
                        {
                            name:        '__Host-crsf-token_*',
                            description: 'Anti-CSRF',
                        },
                    ]
                }
            },
        ];

        if (this.analytics.length) {
            const analytics = {
                title:          trans('cookie_consent.section.analytics.title'),
                description:    trans('cookie_consent.section.analytics.description'),
                linkedCategory: CAT_ANALYTICS,
            };

            sections.push(analytics);

            if (this._hasGoogleAnalytics()) {
                analytics.cookieTable = {
                    headers: {
                        name:        'Cookie',
                        description: 'Description',
                    },
                    body: [
                        {
                            name:        '_ga',
                            description: '<a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                        },
                        {
                            name:        '_gid',
                            description: '<a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                        }
                    ]
                }
            }

            if (this.ads.length) {
                sections.push({
                    title:          trans('cookie_consent.section.ads.title'),
                    description:    trans('cookie_consent.section.ads.description'),
                    linkedCategory: CAT_ADS,
                });
            }
        }

        sections.push({
            title:       trans('cookie_consent.section.more.title'),
            description: trans('cookie_consent.section.more.description'),
        });

        return {
            consentModal: {
                title:              trans('cookie_consent.title_consent'),
                description:        trans('cookie_consent.description'),
                acceptAllBtn:       trans('cookie_consent.accept_all'),
                acceptNecessaryBtn: trans('cookie_consent.reject_all'),
                showPreferencesBtn: trans('cookie_consent.manage_preferences'),
                footer:             `<a href="${this.privacyLink}">${trans('cookie_consent.privacy_policy')}</a>\n<a href="${this.tosLink}">${trans('cookie_consent.terms_and_conditions')}</a>`,
            },

            preferencesModal: {
                title:               trans('cookie_consent.title_preferences'),
                acceptAllBtn:        trans('cookie_consent.accept_all'),
                acceptNecessaryBtn:  trans('cookie_consent.reject_all'),
                savePreferencesBtn:  trans('cookie_consent.save_preferences'),
                closeIconLabel:      trans('cookie_consent.close'),
                serviceCounterLabel: trans('cookie_consent.service_counter'),
                sections:            sections,
            },
        };
    }
}
